import React, { useEffect, useState, useCallback } from 'react';
import { Clock, CheckCircle2, Play, Pause, RefreshCw, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const STATUS_LABEL = { pending: 'Not started', in_progress: 'In progress', on_hold: 'On hold', done: 'Done' };

export default function WorkerJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/order-jobs/my');
      setJobs(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load your jobs');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (job, status) => {
    setBusy(job.id);
    try {
      await api.patch(`/order-jobs/${job.id}`, { status });
      toast.success(status === 'done' ? 'Marked done 🎉' : `Updated to ${STATUS_LABEL[status]}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="worker-jobs-page">
      <div className="max-w-md mx-auto px-4 py-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">My Jobs</h1>
            <p className="text-sm text-slate-500">{jobs.length} open task{jobs.length === 1 ? '' : 's'}</p>
          </div>
          <button onClick={load} className="p-2.5 rounded-full bg-white border border-slate-200 active:scale-95 transition-transform" data-testid="worker-refresh-btn">
            <RefreshCw className={`w-5 h-5 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <p className="text-slate-400 text-sm text-center py-16">Loading…</p>
        ) : jobs.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="worker-empty">
            <CheckCircle2 className="w-14 h-14 mx-auto mb-3 opacity-40" />
            <p className="text-base">All caught up — no open jobs.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((j) => {
              const overdue = j.overdue;
              const dl = j.days_left;
              return (
                <div key={j.id} className={`rounded-2xl p-4 bg-white border shadow-sm ${overdue ? 'border-red-300' : 'border-slate-200'}`} data-testid="worker-job-card">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-800 text-lg leading-tight">{j.checkpoint_name}</p>
                      <p className="text-sm text-slate-500 mt-0.5">Order {j.order_ref}</p>
                    </div>
                    <span className={`shrink-0 text-[11px] font-medium px-2 py-1 rounded-full ${j.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : j.status === 'on_hold' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                      {STATUS_LABEL[j.status]}
                    </span>
                  </div>

                  <div className={`flex items-center gap-1.5 mt-3 text-sm font-medium ${overdue ? 'text-red-600' : 'text-slate-600'}`}>
                    {overdue ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    {overdue
                      ? `Overdue by ${Math.abs(dl ?? 0)} day(s)`
                      : dl != null ? `${dl} day(s) left` : 'No due date'}
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {j.status !== 'in_progress' && j.status !== 'done' && (
                      <button disabled={busy === j.id} onClick={() => setStatus(j, 'in_progress')}
                        className="flex items-center justify-center gap-1.5 py-3 rounded-xl bg-blue-600 text-white font-semibold active:scale-95 transition-transform disabled:opacity-50"
                        data-testid="worker-start-btn">
                        <Play className="w-4 h-4" /> Start
                      </button>
                    )}
                    {j.status === 'in_progress' && (
                      <button disabled={busy === j.id} onClick={() => setStatus(j, 'on_hold')}
                        className="flex items-center justify-center gap-1.5 py-3 rounded-xl bg-amber-500 text-white font-semibold active:scale-95 transition-transform disabled:opacity-50"
                        data-testid="worker-hold-btn">
                        <Pause className="w-4 h-4" /> Hold
                      </button>
                    )}
                    <button disabled={busy === j.id} onClick={() => setStatus(j, 'done')}
                      className="flex items-center justify-center gap-1.5 py-3 rounded-xl bg-emerald-600 text-white font-semibold active:scale-95 transition-transform disabled:opacity-50 col-span-1"
                      data-testid="worker-done-btn">
                      <CheckCircle2 className="w-4 h-4" /> Mark done
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
